import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  ChevronDown,
  GraduationCap,
  Trophy,
  BookOpen,
  Wallet,
  MapPin,
  Briefcase,
  Users,
  ClipboardCheck,
  Network,
  Sparkles,
  Cpu,
  TrendingUp,
  Database,
  Stethoscope,
  Scale,
  CheckCircle2,
  AlertTriangle,
  Compass,
  Building2,
} from 'lucide-react';
import InlineConsultationCTA from '@/components/common/InlineConsultationCTA';

const SEO_TITLE = 'Best Universities in UK 2026: Top Rankings, Courses & ROI';
const SEO_DESCRIPTION =
  'Best universities in UK 2026 for Indian students: rankings, top courses, city guides, tuition fees, scholarships & career outcomes.';

// ─── Evaluation criteria (4×2 grid) ────────────────────────────────
const CRITERIA = [
  { Icon: Trophy, title: 'Global & Subject Rankings', desc: 'QS, THE and REF performance signals across your target discipline.' },
  { Icon: BookOpen, title: 'Course Structure & Specializations', desc: 'Modules, dissertation, industry projects, accreditation depth.' },
  { Icon: Wallet, title: 'Tuition Fees & Scholarships', desc: 'Sticker price minus merit/Chevening/Commonwealth opportunities.' },
  { Icon: MapPin, title: 'Location, Transport & Living Costs', desc: 'London premium vs. regional affordability — runway impact.' },
  { Icon: Briefcase, title: 'Internship & Placement Support', desc: 'Careers offices, employer partnerships, Graduate Route conversion.' },
  { Icon: Users, title: 'Indian Student Community & Safety', desc: 'Established cohorts, societies, mentorship density.' },
  { Icon: ClipboardCheck, title: 'Entry Requirements & Acceptance Chances', desc: 'Realistic banding for your academic profile + English score.' },
  { Icon: Network, title: 'Industry Links & Employer Reputation', desc: 'Sponsored research, alumni gravity in target sectors.' },
];

// ─── Master ranking matrix ─────────────────────────────────────────
const UNIVERSITIES = [
  { name: 'University of Oxford', strengths: 'World-class research, prestige', fields: 'Law, medicine, humanities, science', city: 'Historic academic ecosystem' },
  { name: 'University of Cambridge', strengths: 'Academic excellence, strong research', fields: 'STEM, economics, mathematics', city: 'Strong academic community' },
  { name: 'Imperial College London', strengths: 'Elite STEM and innovation focus', fields: 'Engineering, computer science, data, medicine', city: 'London industry access' },
  { name: 'UCL', strengths: 'Broad subject range, strong global brand', fields: 'Business, architecture, arts, sciences', city: 'Central London exposure' },
  { name: 'University of Edinburgh', strengths: 'Research strength, global reputation', fields: 'AI, business, public health, humanities', city: 'Strong student city' },
  { name: 'King’s College London', strengths: 'Excellent healthcare and policy reputation', fields: 'Law, medicine, public policy', city: 'Central London' },
  { name: 'London School of Economics', strengths: 'Top social sciences school', fields: 'Economics, finance, politics, management', city: 'Career hub in London' },
  { name: 'University of Manchester', strengths: 'Large Indian student presence, strong value', fields: 'Business, engineering, IT, life sciences', city: 'Affordable major city' },
  { name: 'University of Warwick', strengths: 'Strong employability and business focus', fields: 'MBA, economics, finance, engineering', city: 'Good balance of reputation and cost' },
  { name: 'University of Bristol', strengths: 'High academic quality, student-friendly city', fields: 'Engineering, law, life sciences', city: 'Strong student experience' },
  { name: 'University of Glasgow', strengths: 'Research-oriented, strong international profile', fields: 'Medicine, engineering, business', city: 'Lower cost than London' },
  { name: 'University of Birmingham', strengths: 'Broad course range, established reputation', fields: 'Business, engineering, public policy', city: 'Large city with good connectivity' },
  { name: 'University of Leeds', strengths: 'Strong teaching and employability', fields: 'Management, media, engineering', city: 'Vibrant student city' },
  { name: 'University of Southampton', strengths: 'Strong technical reputation', fields: 'Engineering, computing, ocean sciences', city: 'Good value in the south' },
  { name: 'University of Nottingham', strengths: 'Popular with Indian students', fields: 'Business, pharmacy, engineering', city: 'Campus-based, affordable' },
];

// ─── Specialization cohorts ────────────────────────────────────────
const SPECIALIZATIONS = [
  { Icon: Cpu, title: 'Engineering & Tech', items: ['Imperial', 'Cambridge', 'Manchester', 'Southampton', 'Bristol'] },
  { Icon: TrendingUp, title: 'Business & Finance', items: ['LSE', 'Warwick', 'Manchester', 'UCL', 'King’s College'] },
  { Icon: Database, title: 'Data Science & AI', items: ['Imperial', 'Edinburgh', 'Manchester', 'Southampton', 'UCL'] },
  { Icon: Stethoscope, title: 'Medicine & Healthcare', items: ['Oxford', 'Cambridge', 'King’s College', 'Edinburgh', 'Glasgow'] },
  { Icon: Scale, title: 'Law & Policy', items: ['Oxford', 'Cambridge', 'UCL', 'King’s College', 'LSE'] },
];

// ─── Regional comparison ───────────────────────────────────────────
const REGIONAL_ROWS = [
  { factor: 'Living Cost', london: 'Higher Expenses', other: 'More Affordable' },
  { factor: 'Employer Access', london: 'Excellent Proximity', other: 'Good Local Density' },
  { factor: 'Student Community', london: 'Large and Diverse', other: 'Campus-Focused' },
  { factor: 'Transport Network', london: 'Strong but Expensive', other: 'Usually Cheaper' },
  { factor: 'Lifestyle Dynamics', london: 'Fast-Paced / Corporate', other: 'Balanced / Community-Led' },
];

// ─── In-demand curation ────────────────────────────────────────────
const POPULAR_COURSES = [
  'MSc in Data Science / AI / Machine Learning',
  'MSc in Computer Science & Software Engineering',
  'MSc in Finance, Investment & Banking',
  'MBA (1-year Master’s programmes)',
  'MSc in Public Health / Healthcare Management',
  'MSc in Engineering (Mechanical, Electrical, Aerospace)',
  'LLM in International Law',
  'MA in Marketing & Strategic Management',
];

const APPROACH_TIERS = [
  {
    label: 'Ambitious',
    tone: 'gold',
    desc: 'Oxford, Cambridge, Imperial, LSE — for top-tier academics + Chevening-grade English scores.',
  },
  {
    label: 'Moderate',
    tone: 'blue',
    desc: 'UCL, Edinburgh, King’s, Warwick, Manchester, Bristol — strong all-rounders for 70%+ academics.',
  },
  {
    label: 'Safe',
    tone: 'emerald',
    desc: 'Glasgow, Birmingham, Leeds, Southampton, Nottingham — high acceptance with reliable employability.',
  },
];

// ─── Pros / cons ───────────────────────────────────────────────────
const ADVANTAGES = [
  'Globally respected degrees with strong employer recognition',
  'Shorter programme duration (1-year Master’s) → faster ROI',
  'Graduate Route — 2 years of post-study work (applications by 31 Dec 2026)',
  'Scholarship pool including Chevening, Commonwealth & institutional awards',
];

const LIMITATIONS = [
  'Higher overall tuition than continental Europe',
  'Highly competitive entry at Russell Group institutions',
  'High London living costs vs. regional cities',
  'Strict 28-day maintenance funds rule on visa applications',
];

// ─── Anchor chips ──────────────────────────────────────────────────
const ANCHOR_LINKS = [
  { label: 'UK Student Visa for Indian Students 2026', path: '/study-abroad/uk/student-visa/' },
  { label: 'Cost of Studying in UK for Indian Students 2026', path: '/study-abroad/uk/cost-of-study/' },
  { label: 'UK Scholarships for Indian Students 2026', path: '/study-abroad/uk/scholarships/' },
  { label: 'Masters in UK for Indian Students 2026', path: '/study-abroad/uk/masters/' },
  { label: 'MBA in UK for Indian Students', path: '/study-abroad/uk/mba/' },
  { label: 'Education Loan Assistance', path: '/study-abroad/education-loan/' },
  { label: 'Book a Free Counselling Session', path: '/study-abroad/book-counselling/?source=study-abroad-uk-best-universities-anchor' },
];

// ─── FAQs ──────────────────────────────────────────────────────────
const FAQS = [
  { q: 'Which is the best university in the UK for Indian students in 2026?', a: 'It depends on field, budget, and academic profile. Oxford, Cambridge and Imperial top the prestige bracket. For engineering, Imperial and Manchester. For business and finance, LSE and Warwick. For balanced value, Edinburgh, UCL and Bristol.' },
  { q: 'What are the entry requirements for UK universities?', a: 'Most Master’s programmes require a Bachelor’s with 60–75%+ (or 2:1 equivalent), IELTS 6.5–7.0 (component minimums), a strong SOP, and 1–2 LORs. Top universities expect 80%+ and competitive English scores.' },
  { q: 'How affordable are UK universities for Indian students?', a: 'Tuition ranges from £18,000 to £45,000 per year. Russell Group institutions sit at the higher end. Regional cities cut living costs by 25–35% vs London. Scholarships, education loans, and 1-year Master’s formats materially improve ROI.' },
  { q: 'Is studying in London worth the extra cost?', a: 'For finance, law, policy and creative industries — yes; the network and employer proximity justify the premium. For STEM, healthcare, and engineering — regional universities (Manchester, Edinburgh, Glasgow) deliver comparable outcomes at lower cost.' },
  { q: 'Can I work in the UK after graduation?', a: 'Yes. The Graduate Route grants 2 years of post-study work for applications submitted on or before 31 December 2026, then changes to 18 months from 1 January 2027.' },
  { q: 'Which universities have the largest Indian student communities?', a: 'Manchester, Birmingham, Leeds, Nottingham, Coventry, Warwick, and many London-based universities host long-established Indian cohorts with active societies and mentorship networks.' },
  { q: 'How important is the QS / THE ranking when shortlisting?', a: 'Rankings matter for prestige but should not be the sole filter. Subject-specific rankings, employer reputation in your target sector, and 5-year graduate outcomes data are equally important.' },
  { q: 'Do I need IELTS for all UK universities?', a: 'Most universities require IELTS, PTE Academic or another approved SELT. Some waive it if your last qualification was taught in English — verify with the specific institution’s admissions team.' },
];

// ─── Subcomponents ─────────────────────────────────────────────────
const SectionHeading = ({ eyebrow, title, subtitle }) => (
  <div className="max-w-3xl mb-8 lg:mb-12">
    {eyebrow && (
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C5A028]/12 border border-[#C5A028]/35 text-[10px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-3">
        {eyebrow}
      </div>
    )}
    <h2 className="text-2xl sm:text-3xl lg:text-[36px] font-bold font-syne text-[#003087] leading-tight mb-3">
      {title}
    </h2>
    {subtitle && <p className="text-slate-600 leading-relaxed text-sm sm:text-base">{subtitle}</p>}
  </div>
);

const BulletDot = ({ tone = 'gold' }) => {
  const toneMap = {
    gold: 'bg-[#C5A028]',
    blue: 'bg-[#003087]',
    emerald: 'bg-emerald-500',
    amber: 'bg-amber-500',
    red: 'bg-red-500',
  };
  return <span className={`mt-1.5 inline-flex shrink-0 w-2 h-2 rounded-full ${toneMap[tone] || toneMap.gold}`} />;
};

const FaqItem = ({ q, a, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`border rounded-2xl transition-all ${
        open ? 'border-[#C5A028]/50 bg-white shadow-md shadow-[#003087]/5' : 'border-slate-200 bg-white'
      }`}
      data-testid={`uk-bu-faq-item-${index}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-4 text-left px-5 sm:px-6 py-4 sm:py-5"
        aria-expanded={open}
        data-testid={`uk-bu-faq-toggle-${index}`}
      >
        <span className="text-sm sm:text-base font-semibold text-slate-900 leading-snug">{q}</span>
        <ChevronDown className={`shrink-0 w-5 h-5 mt-0.5 text-[#C5A028] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-sm sm:text-[15px] text-slate-600 leading-relaxed">{a}</div>
      )}
    </div>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────
const UKBestUniversitiesPage = () => {
  useEffect(() => {
    document.title = SEO_TITLE;

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    const prevDesc = metaDesc.getAttribute('content');
    metaDesc.setAttribute('content', SEO_DESCRIPTION);

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const scriptEl = document.createElement('script');
    scriptEl.type = 'application/ld+json';
    scriptEl.setAttribute('data-uk-bu-faq-schema', '1');
    scriptEl.text = JSON.stringify(schema);
    document.head.appendChild(scriptEl);

    // BreadcrumbList schema for SEO (Home → Study Abroad → UK → Best Universities)
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://zafco.in/' },
        { '@type': 'ListItem', position: 2, name: 'Study Abroad', item: 'https://zafco.in/study-abroad/' },
        { '@type': 'ListItem', position: 3, name: 'UK', item: 'https://zafco.in/study-abroad/uk/' },
        { '@type': 'ListItem', position: 4, name: 'Best Universities' },
      ],
    };
    const breadcrumbEl = document.createElement('script');
    breadcrumbEl.type = 'application/ld+json';
    breadcrumbEl.setAttribute('data-uk-bu-breadcrumb-schema', '1');
    breadcrumbEl.text = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbEl);

    return () => {
      if (prevDesc !== null) metaDesc.setAttribute('content', prevDesc);
      const existing = document.head.querySelector('script[data-uk-bu-faq-schema]');
      if (existing) existing.remove();
      const existingBc = document.head.querySelector('script[data-uk-bu-breadcrumb-schema]');
      if (existingBc) existingBc.remove();
    };
    // All referenced names (SEO_TITLE, SEO_DESCRIPTION, FAQS) are module-level
    // constants and therefore stable — this effect must run exactly once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="bg-white" data-testid="uk-best-universities-page">
      {/* ============================== HERO ============================== */}
      <section
        className="relative overflow-hidden text-white"
        style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 55%, #020617 100%)' }}
      >
        <div aria-hidden="true" className="pointer-events-none absolute -top-32 -right-24 w-96 h-96 rounded-full bg-[#C5A028]/14 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 py-14 lg:py-24">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 backdrop-blur-sm text-[10.5px] font-bold tracking-[0.22em] text-[#FACC15] uppercase mb-5">
              <Trophy className="w-3.5 h-3.5" />
              UK Universities · 2026 Cohort
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-[52px] font-bold font-syne leading-[1.05] tracking-tight mb-5" data-testid="uk-bu-h1">
              Best Universities in UK for <span className="text-[#FACC15]">Indian Students 2026</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl mb-6">
              Choosing the best UK university is a four-axis decision: global rankings, course fit, affordability, and 5-year ROI.
              This counsellor-grade guide ranks 15 Russell Group leaders, maps them to your discipline, and stress-tests each option
              against your academic profile and budget.
            </p>

            {/* Why UK universities — highlight panel */}
            <div className="bg-white/[0.05] border border-white/15 rounded-2xl p-5 sm:p-6 backdrop-blur-sm" data-testid="uk-bu-hero-highlight">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-[#FACC15]" />
                <h3 className="text-sm font-bold tracking-[0.18em] uppercase text-[#FACC15]">
                  Why UK universities attract Indian students
                </h3>
              </div>
              <p className="text-sm sm:text-[15px] text-slate-200 leading-relaxed">
                Globally respected degrees, shorter 1-year Master’s formats, the Graduate Route work pathway, an established
                Indian alumni network across every major city, and direct access to London’s finance, tech, and policy ecosystems —
                a combination unmatched by any other study destination.
              </p>
            </div>

            <div className="flex flex-wrap gap-3 mt-7">
              <Link
                to="/study-abroad/book-counselling/?source=study-abroad-uk-best-universities"
                className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-7 py-3.5 rounded-full transition-all hover:shadow-xl hover:shadow-[#C5A028]/30 hover:-translate-y-0.5"
                data-testid="uk-bu-hero-cta"
              >
                Get My Personalized UK Shortlist
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/study-abroad/free-profile-evaluation/?source=study-abroad-uk-best-universities-hero&country=uk"
                className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white font-semibold text-sm px-7 py-3.5 rounded-full transition-all"
              >
                Evaluate My Profile
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============================== CRITERIA GRID ============================== */}
      <section className="py-14 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="The Framework"
            title="How to Choose the Right University"
            subtitle="Eight evaluation parameters that separate a brand-driven choice from a high-ROI, career-aligned decision."
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5" data-testid="uk-bu-criteria-grid">
            {CRITERIA.map(({ Icon, title, desc }) => (
              <div key={title} className="bg-white border border-slate-200 hover:border-[#C5A028]/45 rounded-2xl p-5 transition-all hover:shadow-md hover:shadow-[#003087]/5">
                <div className="w-10 h-10 rounded-xl bg-[#003087]/8 flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5 text-[#003087]" />
                </div>
                <h3 className="text-sm sm:text-base font-bold font-syne text-[#003087] mb-2 leading-snug">{title}</h3>
                <p className="text-xs sm:text-[13px] text-slate-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== RANKING MATRIX ============================== */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="Master Matrix"
            title="Top Respected Universities in the UK (2026 Cohort)"
            subtitle="Fifteen institutions across the Russell Group and beyond — ranked across academic strength, signature disciplines, and city-level advantage."
          />

          <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm" data-testid="uk-bu-matrix">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-100/70 text-[10.5px] tracking-[0.18em] uppercase text-slate-600">
                    <th className="px-5 sm:px-7 py-4 text-left font-bold">University</th>
                    <th className="px-5 sm:px-7 py-4 text-left font-bold hidden md:table-cell">Strengths</th>
                    <th className="px-5 sm:px-7 py-4 text-left font-bold">Best-Known Fields</th>
                    <th className="px-5 sm:px-7 py-4 text-left font-bold hidden lg:table-cell">City Advantage</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {UNIVERSITIES.map((u) => (
                    <tr key={u.name} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 sm:px-7 py-5 align-top">
                        <div className="font-bold text-slate-900 leading-snug">{u.name}</div>
                        <div className="text-xs text-slate-500 mt-1 md:hidden">{u.strengths}</div>
                      </td>
                      <td className="px-5 sm:px-7 py-5 align-top text-slate-700 hidden md:table-cell">{u.strengths}</td>
                      <td className="px-5 sm:px-7 py-5 align-top text-slate-700">{u.fields}</td>
                      <td className="px-5 sm:px-7 py-5 align-top text-slate-700 hidden lg:table-cell">{u.city}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Image placeholder */}
          <div
            className="mt-8 aspect-[16/5] rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center text-slate-400 text-xs"
            role="img"
            aria-label="Top UK universities map placeholder"
          >
            Image placeholder · Top UK universities map
          </div>
        </div>
      </section>

      {/* ============================== SPECIALIZATIONS ============================== */}
      <section className="py-14 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="By Discipline"
            title="Best Universities Categorized by Specialization"
            subtitle="The most reliable signal isn’t the overall ranking — it’s the subject-level reputation. Match your discipline to the right cohort."
          />

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="uk-bu-specializations">
            {SPECIALIZATIONS.map(({ Icon, title, items }) => (
              <div key={title} className="bg-white border border-slate-200 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-2xl bg-[#003087]/10 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#003087]" />
                  </div>
                  <h3 className="text-base sm:text-lg font-bold font-syne text-[#003087]">{title}</h3>
                </div>
                <ul className="space-y-2">
                  {items.map((it) => (
                    <li key={it} className="flex items-start gap-2.5">
                      <BulletDot tone="gold" />
                      <span className="text-sm text-slate-700 leading-relaxed">{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== REGIONAL ============================== */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="Geography"
            title="Geographical Mapping: London vs Regional Hubs"
            subtitle="Where you study reshapes the entire economics of a UK degree — and the kind of career network you graduate into."
          />

          <div className="grid lg:grid-cols-5 gap-6 lg:gap-8 items-start">
            <div className="lg:col-span-2">
              <p className="text-sm sm:text-[15px] text-slate-700 leading-relaxed mb-4">
                <strong className="text-[#003087]">London</strong> offers an unmatched concentration of employers — finance, law,
                policy, tech and creative industries all converge inside a single transport network. Tuition and rent are at the
                top of the UK distribution, but the network effects are equally outsized.
              </p>
              <p className="text-sm sm:text-[15px] text-slate-700 leading-relaxed">
                <strong className="text-[#003087]">Manchester, Edinburgh, Glasgow, Bristol, Birmingham</strong> deliver
                comparable academic firepower in STEM, healthcare and business at 25–35% lower living costs, with tighter
                campus communities and easier student visa runway.
              </p>
            </div>

            <div className="lg:col-span-3">
              <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm" data-testid="uk-bu-regional-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-100/70 text-[10.5px] tracking-[0.18em] uppercase text-slate-600">
                        <th className="px-5 sm:px-6 py-4 text-left font-bold">Evaluation Factor</th>
                        <th className="px-5 sm:px-6 py-4 text-left font-bold">London</th>
                        <th className="px-5 sm:px-6 py-4 text-left font-bold">Other UK Cities</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {REGIONAL_ROWS.map((row) => (
                        <tr key={row.factor} className="hover:bg-slate-50 transition-colors">
                          <td className="px-5 sm:px-6 py-4 font-bold text-slate-900">{row.factor}</td>
                          <td className="px-5 sm:px-6 py-4 text-slate-700">{row.london}</td>
                          <td className="px-5 sm:px-6 py-4 text-slate-700">{row.other}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Image placeholder */}
          <div
            className="mt-8 aspect-[16/5] rounded-2xl border-2 border-dashed border-slate-200 bg-white flex items-center justify-center text-slate-400 text-xs"
            role="img"
            aria-label="London vs other UK cities comparison chart placeholder"
          >
            Image placeholder · London vs other UK cities comparison chart
          </div>
        </div>
      </section>

      {/* ============================== STRATEGIC CURATION ============================== */}
      <section className="py-14 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="The Shortlist"
            title="Popular Courses, Profiling & Fee Metrics"
            subtitle="Treat your shortlist as a portfolio: one ambitious target, two moderate, one safe. Match each to your academic profile, English score, and budget runway."
          />

          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
            {/* Popular Courses */}
            <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#003087]/10 text-[10.5px] font-bold tracking-[0.18em] text-[#003087] uppercase mb-4">
                Popular Courses
              </div>
              <h3 className="text-lg sm:text-xl font-bold font-syne text-[#003087] mb-5">High-demand programmes</h3>
              <ul className="space-y-2.5">
                {POPULAR_COURSES.map((c) => (
                  <li key={c} className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-[#C5A028] mt-0.5 shrink-0" />
                    <span className="text-sm sm:text-[15px] text-slate-700 leading-relaxed">{c}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Suggested Approach */}
            <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C5A028]/15 text-[10.5px] font-bold tracking-[0.18em] text-[#C5A028] uppercase mb-4">
                Suggested Approach
              </div>
              <h3 className="text-lg sm:text-xl font-bold font-syne text-[#003087] mb-5">Build a 3-tier shortlist</h3>
              <ul className="space-y-3">
                {APPROACH_TIERS.map((tier) => (
                  <li key={tier.label} className="flex items-start gap-3">
                    <BulletDot tone={tier.tone} />
                    <div>
                      <div className="text-sm font-bold text-[#003087]">{tier.label}</div>
                      <div className="text-xs sm:text-[13px] text-slate-600 leading-relaxed mt-0.5">{tier.desc}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Fees & Value + Indian Community + Admission */}
          <div className="grid md:grid-cols-3 gap-5 mt-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <h4 className="text-sm font-bold font-syne text-[#003087] mb-2">Fees & Value</h4>
              <p className="text-xs sm:text-[13px] text-slate-600 leading-relaxed">
                Master’s tuition ranges £18,000–£45,000. Regional cities reduce living costs by 25–35%. Add Chevening, Commonwealth, or institutional scholarships for material ROI.
              </p>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <h4 className="text-sm font-bold font-syne text-[#003087] mb-2">Indian Community</h4>
              <p className="text-xs sm:text-[13px] text-slate-600 leading-relaxed">
                Manchester, Birmingham, Leeds, Nottingham, Coventry, Warwick and most London universities host long-established Indian cohorts with active alumni networks.
              </p>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <h4 className="text-sm font-bold font-syne text-[#003087] mb-2">Admission Requirements</h4>
              <p className="text-xs sm:text-[13px] text-slate-600 leading-relaxed">
                Typical thresholds: 60%+ academic average (2:1 equivalent), IELTS 6.5–7.0, strong SOP, 1–2 LORs, portfolio for arts/design programmes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================== PROS / CONS ============================== */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="Balanced Analysis"
            title="Key Advantages vs Limitations"
            subtitle="Every great decision survives an honest stress-test. Here is the unsanitised view of UK degrees."
          />

          <div className="grid md:grid-cols-2 gap-5 lg:gap-7" data-testid="uk-bu-pros-cons">
            {/* Advantages */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-3xl p-6 sm:p-8" data-testid="uk-bu-advantages">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-2xl bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-700" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold font-syne text-emerald-800">Advantages</h3>
              </div>
              <ul className="space-y-2.5">
                {ADVANTAGES.map((a) => (
                  <li key={a} className="flex items-start gap-2.5">
                    <BulletDot tone="emerald" />
                    <span className="text-sm sm:text-[15px] text-emerald-900/90 leading-relaxed">{a}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Limitations */}
            <div className="bg-amber-50 border border-amber-200 rounded-3xl p-6 sm:p-8" data-testid="uk-bu-limitations">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-2xl bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-700" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold font-syne text-amber-800">Limitations</h3>
              </div>
              <ul className="space-y-2.5">
                {LIMITATIONS.map((l) => (
                  <li key={l} className="flex items-start gap-2.5">
                    <BulletDot tone="amber" />
                    <span className="text-sm sm:text-[15px] text-amber-900/90 leading-relaxed">{l}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============================== ANCHOR CHIPS ============================== */}
      <section className="py-10 lg:py-14 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-[10.5px] tracking-[0.22em] uppercase font-bold text-slate-500 mb-3">
            Related Reading & Tools
          </div>
          <div className="flex flex-wrap gap-2.5" data-testid="uk-bu-anchor-chips">
            {ANCHOR_LINKS.map((l) => (
              <Link
                key={l.path}
                to={l.path}
                className="inline-flex items-center gap-1.5 bg-white hover:bg-[#003087] hover:text-white border border-slate-200 text-slate-700 text-xs sm:text-[13px] font-semibold px-4 py-2 rounded-full transition-colors"
              >
                <Compass className="w-3 h-3" />
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== FAQ ============================== */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <SectionHeading
            eyebrow="Answers"
            title="Frequently Asked Questions"
            subtitle="The eight questions Indian families ask most often during the UK shortlisting process."
          />

          <div className="space-y-3" data-testid="uk-bu-faq-list">
            {FAQS.map((faq, idx) => (
              <FaqItem key={faq.q} q={faq.q} a={faq.a} index={idx} />
            ))}
          </div>
        </div>
      </section>

      {/* ============================== INLINE LEAD CAPTURE FORM (Zoho CRM) ============================== */}
      <InlineConsultationCTA
        eyebrow="Free University Shortlisting Session"
        headline="Shortlist Your Dream UK University — Free 1-on-1 Counselling"
        subheadline="Share your profile and a senior counsellor will reach out within 60 seconds on WhatsApp with a personalised list of UK universities that match your budget and grades."
        bullets={[
          'Russell Group, post-1992 & specialised arts/business unis covered',
          'MEA-approved · 10,000+ students placed since 2015',
          'Free profile + scholarship review before any commitment',
        ]}
        testId="uk-bu-inline-consultation"
      />

      {/* ============================== LEAD CAPTURE ============================== */}
      <section className="py-14 lg:py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <div
            className="relative overflow-hidden rounded-3xl text-white"
            style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 60%, #020617 100%)' }}
            data-testid="uk-bu-lead-capture"
          >
            <div aria-hidden="true" className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#C5A028]/16 blur-3xl" />
            <div className="relative p-8 sm:p-12 lg:p-16 grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C5A028]/18 border border-[#C5A028]/40 text-[10.5px] font-bold tracking-[0.22em] text-[#FACC15] uppercase mb-4">
                  <GraduationCap className="w-3.5 h-3.5" />
                  ZAFCO UK Counselling Desk
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-[40px] font-bold font-syne leading-tight mb-4">
                  Need help selecting the best universities in UK for Indian students 2026 based on your marks, budget, and career goal?
                </h2>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed mb-6 max-w-xl">
                  Book free counselling with Zafco for a personalized university shortlist, scholarship guidance, and application strategy.
                </p>
                <Link
                  to="/study-abroad/book-counselling/?source=study-abroad-uk-best-universities"
                  className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm sm:text-base px-7 py-4 rounded-full transition-all hover:shadow-2xl hover:shadow-[#C5A028]/40 hover:-translate-y-0.5"
                  data-testid="uk-bu-lead-capture-cta"
                >
                  Generate My Personalized UK Shortlist
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="hidden lg:block">
                <ul className="space-y-3">
                  {[
                    { Icon: Trophy, label: 'Profile-matched university ranking by your academics' },
                    { Icon: Building2, label: 'Russell Group vs regional cost-benefit analysis' },
                    { Icon: Wallet, label: 'Scholarship & education-loan integrated planning' },
                    { Icon: Briefcase, label: 'Graduate Route + 5-year career outcome modelling' },
                  ].map(({ Icon, label }) => (
                    <li key={label} className="flex items-start gap-3 bg-white/[0.06] border border-white/10 rounded-xl px-4 py-3">
                      <Icon className="w-4 h-4 text-[#FACC15] mt-0.5 shrink-0" />
                      <span className="text-sm text-slate-200">{label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UKBestUniversitiesPage;
